<template>
  <Head title="Edit Data Grafik" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Grafik Data Mahasiswa', href: route('grafik.index') },
            { name: 'Edit Data', href: '#' },
          ]"
        />

        <!-- Header Section -->
        <div class="mb-4 sm:mb-6">
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <h3 class="text-lg font-semibold text-gray-900">
                Edit Data Grafik Mahasiswa
              </h3>
              <p class="mt-1 text-sm text-gray-600">
                Perbarui informasi data mahasiswa
                {{ grafik.studyProgram?.name || `Prodi ${grafik.kode_prodi}` }}
              </p>
            </div>
            <div class="flex mt-4 space-x-2 sm:mt-0">
              <Link
                :href="route('grafik.index')"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-200 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <i class="mr-2 fa-solid fa-arrow-left"></i>
                Kembali
              </Link>
              <Link
                :href="route('grafik.show', grafik.id)"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 transition-colors duration-200 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <i class="mr-2 fa-solid fa-eye"></i>
                Lihat Detail
              </Link>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <!-- Form Section -->
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-1">
            <!-- Main Form -->
            <div
              class="mb-6 bg-white border border-gray-200 shadow-lg rounded-xl"
            >
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h3
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-edit sm:mr-3"></i>
                  Form Edit Data Grafik
                </h3>
              </div>
              <div class="p-4 sm:p-6">
                <form @submit.prevent="submit" class="space-y-6">
                  <!-- Alert for validation errors -->
                  <div
                    v-if="form.errors.duplicate || form.errors.limit"
                    class="p-4 mb-4 text-sm border rounded-lg"
                    :class="[
                      form.errors.duplicate
                        ? 'text-yellow-800 border-yellow-300 bg-yellow-50'
                        : 'text-red-800 border-red-300 bg-red-50',
                    ]"
                  >
                    <div class="flex items-center">
                      <i
                        class="mr-2 text-lg"
                        :class="[
                          form.errors.duplicate
                            ? 'fa-solid fa-triangle-exclamation text-yellow-600'
                            : 'fa-solid fa-circle-exclamation text-red-600',
                        ]"
                      ></i>
                      <div>
                        <p class="font-medium" v-if="form.errors.duplicate">
                          {{ form.errors.duplicate }}
                        </p>
                        <p class="font-medium" v-if="form.errors.limit">
                          {{ form.errors.limit }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <!-- Tahun Akademik -->
                    <div>
                      <label
                        for="tahun_akademik"
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Tahun Akademik <span class="text-red-500">*</span>
                      </label>
                      <input
                        id="tahun_akademik"
                        v-model="form.tahun_akademik"
                        type="text"
                        placeholder="Contoh: 2024/2025"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 hover:border-gray-400 transition-colors duration-200"
                        :class="{
                          'border-red-500':
                            form.errors.tahun_akademik ||
                            form.errors.duplicate ||
                            form.errors.limit,
                        }"
                      />
                      <p
                        v-if="form.errors.tahun_akademik"
                        class="mt-1 text-sm text-red-600"
                      >
                        {{ form.errors.tahun_akademik }}
                      </p>
                    </div>

                    <!-- Semester -->
                    <div>
                      <label
                        for="semester"
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Semester <span class="text-red-500">*</span>
                      </label>
                      <select
                        id="semester"
                        v-model="form.semester"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 hover:border-gray-400 transition-colors duration-200"
                        :class="{
                          'border-red-500':
                            form.errors.semester || form.errors.duplicate,
                        }"
                      >
                        <option value="">Pilih Semester</option>
                        <option value="ganjil">Ganjil</option>
                        <option value="genap">Genap</option>
                      </select>
                      <p
                        v-if="form.errors.semester"
                        class="mt-1 text-sm text-red-600"
                      >
                        {{ form.errors.semester }}
                      </p>
                    </div>

                    <!-- Program Studi -->
                    <div>
                      <label
                        for="kode_prodi"
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Program Studi <span class="text-red-500">*</span>
                      </label>
                      <select
                        id="kode_prodi"
                        v-model="form.kode_prodi"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 hover:border-gray-400 transition-colors duration-200"
                        :class="{
                          'border-red-500':
                            form.errors.kode_prodi ||
                            form.errors.duplicate ||
                            form.errors.limit,
                        }"
                      >
                        <option value="">Pilih Program Studi</option>
                        <option
                          v-for="prodi in studyPrograms"
                          :key="prodi.id"
                          :value="prodi.kodeprodi"
                        >
                          {{ prodi.name }} ({{ prodi.kodeprodi }})
                        </option>
                      </select>
                      <p
                        v-if="form.errors.kode_prodi"
                        class="mt-1 text-sm text-red-600"
                      >
                        {{ form.errors.kode_prodi }}
                      </p>
                    </div>

                    <!-- Jumlah Mahasiswa -->
                    <div>
                      <label
                        for="jumlah_mahasiswa"
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Jumlah Mahasiswa <span class="text-red-500">*</span>
                      </label>
                      <input
                        id="jumlah_mahasiswa"
                        v-model.number="form.jumlah_mahasiswa"
                        type="number"
                        min="0"
                        placeholder="Contoh: 150"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 hover:border-gray-400 transition-colors duration-200"
                        :class="{
                          'border-red-500': form.errors.jumlah_mahasiswa,
                        }"
                      />
                      <p
                        v-if="form.errors.jumlah_mahasiswa"
                        class="mt-1 text-sm text-red-600"
                      >
                        {{ form.errors.jumlah_mahasiswa }}
                      </p>
                    </div>

                    <!-- Keterangan -->
                    <div class="md:col-span-2">
                      <label
                        for="keterangan"
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Keterangan
                      </label>
                      <textarea
                        id="keterangan"
                        v-model="form.keterangan"
                        rows="3"
                        placeholder="Keterangan tambahan (opsional)"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 hover:border-gray-400 transition-colors duration-200"
                        :class="{ 'border-red-500': form.errors.keterangan }"
                      ></textarea>
                      <p
                        v-if="form.errors.keterangan"
                        class="mt-1 text-sm text-red-600"
                      >
                        {{ form.errors.keterangan }}
                      </p>
                    </div>
                  </div>

                  <!-- Submit Button -->
                  <div
                    class="flex items-center justify-end pt-6 space-x-4 border-t border-gray-200"
                  >
                    <Link
                      :href="route('grafik.show', grafik.id)"
                      class="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      Batal
                    </Link>
                    <button
                      type="submit"
                      :disabled="form.processing"
                      class="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white bg-yellow-600 border border-transparent rounded-lg shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200 disabled:opacity-50"
                    >
                      <i
                        v-if="form.processing"
                        class="mr-2 fa-solid fa-spinner fa-spin"
                      ></i>
                      <i v-else class="mr-2 fa-solid fa-save"></i>
                      {{ form.processing ? "Memperbarui..." : "Perbarui Data" }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="order-2 col-span-1 space-y-6 lg:order-2">
            <!-- Data History -->
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h3
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-info-circle sm:mr-3"></i>
                  Informasi Record
                </h3>
              </div>
              <div class="p-4 sm:p-6">
                <dl class="grid grid-cols-1 gap-4">
                  <div>
                    <dt class="text-sm font-medium text-gray-500">ID Record</dt>
                    <dd class="mt-1 text-sm text-gray-900">#{{ grafik.id }}</dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-500">Dibuat</dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ formatDate(grafik.created_at) }}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-500">
                      Terakhir Diperbarui
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ formatDate(grafik.updated_at) }}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <!-- Warning Card -->
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h3
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-exclamation-triangle sm:mr-3"></i>
                  Peringatan
                </h3>
              </div>
              <div class="p-4 sm:p-6">
                <div class="text-sm text-gray-700">
                  <p class="mb-2">
                    Pastikan data yang diubah sudah benar. Perubahan data akan
                    mempengaruhi:
                  </p>
                  <ul class="space-y-1 text-sm list-disc list-inside">
                    <li>Tampilan grafik dan chart</li>
                    <li>Laporan yang terkait</li>
                    <li>Data statistik keseluruhan</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { inject } from "vue";
import { Head, Link, useForm } from "@inertiajs/vue3";

import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
const route = inject('route');

// Props
const props = defineProps({
  grafik: Object,
  studyPrograms: Array,
});

// Form
const form = useForm({
  tahun_akademik: props.grafik.tahun_akademik,
  semester: props.grafik.semester,
  kode_prodi: props.grafik.kode_prodi,
  jumlah_mahasiswa: props.grafik.jumlah_mahasiswa,
  keterangan: props.grafik.keterangan,
});

// Methods
const submit = () => {
  form.put(route("grafik.update", props.grafik.id), {
    onSuccess: () => {
      // Success akan dihandle oleh redirect dari controller
    },
    onError: (errors) => {
      // Scroll to the top to see the error message
      if (errors.duplicate || errors.limit) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
    preserveScroll: true,
  });
};

const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString("id-ID", options);
};
</script>
